import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@mui/material';

const TextRevealAnimation = ({ 
  children, 
  delay = 0, 
  duration = 1000,
  sunRays = true,
  triggerOnScroll = true, // New prop to enable/disable scroll trigger
  threshold = 0.1, // How much of the element should be visible before triggering
  rootMargin = "0px", // Margin around the root
  sx = {},
  ...typographyProps 
}) => {
  const [isVisible, setIsVisible] = useState(!triggerOnScroll); // If scroll trigger is disabled, start visible
  const [hasTriggered, setHasTriggered] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    if (!triggerOnScroll) {
      // If scroll trigger is disabled, use the original delay behavior
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delay);
      return () => clearTimeout(timer);
    }

    // Intersection Observer for scroll-triggered animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered) {
          // Add the original delay before triggering the animation
          setTimeout(() => {
            setIsVisible(true);
            setHasTriggered(true);
          }, delay);
          
          // Disconnect observer after first trigger to prevent re-triggering
          observer.disconnect();
        }
      },
      { 
        threshold,
        rootMargin 
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [delay, triggerOnScroll, threshold, rootMargin, hasTriggered]);

  return (
    <div 
      ref={elementRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Sun rays background - only render if sunRays is true */}
      {sunRays && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '200px',
            height: '200px',
            transform: 'translate(-50%, -50%)',
            opacity: isVisible ? 0.15 : 0,
            transition: `all ${duration + 500}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            background: `conic-gradient(
              from 0deg,
              transparent 0deg,
              rgba(255, 193, 7, 0.3) 10deg,
              transparent 20deg,
              transparent 30deg,
              rgba(255, 193, 7, 0.3) 40deg,
              transparent 50deg,
              transparent 60deg,
              rgba(255, 193, 7, 0.3) 70deg,
              transparent 80deg,
              transparent 90deg,
              rgba(255, 193, 7, 0.3) 100deg,
              transparent 110deg,
              transparent 120deg,
              rgba(255, 193, 7, 0.3) 130deg,
              transparent 140deg,
              transparent 150deg,
              rgba(255, 193, 7, 0.3) 160deg,
              transparent 170deg,
              transparent 180deg,
              rgba(255, 193, 7, 0.3) 190deg,
              transparent 200deg,
              transparent 210deg,
              rgba(255, 193, 7, 0.3) 220deg,
              transparent 230deg,
              transparent 240deg,
              rgba(255, 193, 7, 0.3) 250deg,
              transparent 260deg,
              transparent 270deg,
              rgba(255, 193, 7, 0.3) 280deg,
              transparent 290deg,
              transparent 300px,
              rgba(255, 193, 7, 0.3) 310deg,
              transparent 320deg,
              transparent 330deg,
              rgba(255, 193, 7, 0.3) 340deg,
              transparent 350deg,
              transparent 360deg
            )`,
            borderRadius: '50%',
            animation: isVisible ? 'sunRaysRotate 15s linear infinite' : 'none',
            zIndex: -1,
          }}
        />
      )}
      
      {/* Text content */}
      <Typography
        {...typographyProps}
        sx={{
          ...sx,
          opacity: isVisible ? 1 : 0,
          filter: isVisible ? 'blur(0px)' : 'blur(8px)',
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </Typography>
      
      {/* CSS Animation - only include if sunRays is true */}
      {sunRays && (
        <style jsx>{`
          @keyframes sunRaysRotate {
            from {
              transform: translate(-50%, -50%) rotate(0deg);
            }
            to {
              transform: translate(-50%, -50%) rotate(360deg);
            }
          }
        `}</style>
      )}
    </div>
  );
};

// Usage examples:

// With scroll trigger (default behavior now)
const YourComponentWithScrollTrigger = ({ t }) => {
  return (
    <TextRevealAnimation
      variant="h3"
      component="h2"
      triggerOnScroll={true} // Enable scroll trigger
      threshold={0.2} // Trigger when 20% of element is visible
      delay={300} // Delay after element comes into view
      duration={1200}
      sx={{
        fontFamily: "Kontora",
        fontWeight: "bold",
        mb: 1,
        fontSize: { xs: 36, md: 48 },
        display: "inline-flex",
        alignItems: "baseline",
        color: "#000000",
      }}
    >
      {t("landingPage.jobs.title")}
    </TextRevealAnimation>
  );
};

// Without scroll trigger (original behavior)
const YourComponentWithoutScrollTrigger = ({ t }) => {
  return (
    <TextRevealAnimation
      variant="h4"
      triggerOnScroll={false} // Disable scroll trigger
      delay={300}
      duration={1200}
      sx={{
        fontWeight: 600,
        fontSize: { sm: "1.5rem", md: "2rem", lg: "2.25rem" },
        lineHeight: 1.2,
        fontFamily: "Poppins",
        pr: { sm: 2, md: 6, lg: 8 },
      }}
    >
      {t("login.slogan")}
    </TextRevealAnimation>
  );
};

export default TextRevealAnimation;